import { useWorkspaceStore } from '@/store/workspace'
import { Copy, Terminal, Key } from 'lucide-react'
import { useState } from 'react'
import { db } from '@/lib/firebase'
import { collection, addDoc } from 'firebase/firestore'

export default function Settings() {
    const { workspaceId, workspaceName, plan, customFirebaseKey, customFirebaseProjectId, setCustomFirebaseKey } = useWorkspaceStore()
    const [copied, setCopied] = useState(false)
    const [localCustomKey, setLocalCustomKey] = useState(customFirebaseKey || "")
    const [localProjectId, setLocalProjectId] = useState(customFirebaseProjectId || "")
    const [isConnecting, setIsConnecting] = useState(false)
    const [isImporting, setIsImporting] = useState(false)
    const [importMessage, setImportMessage] = useState("")

    // In a real app this would be fetched securely or generated and stored in KV.
    // For UI scaffolding, we show a mock preview.
    const mockKey = `hrx_live_${workspaceId || 'test'}7a2xBp90PqmXlOwzYtR...`

    const handleCopy = () => {
        navigator.clipboard.writeText(mockKey)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    const handleConnectFirebase = () => {
        if (!localCustomKey || !localProjectId) {
            alert('Please provide both Project ID and API Key')
            return
        }
        setIsConnecting(true)
        setTimeout(() => {
            setIsConnecting(false)
            setCustomFirebaseKey(localCustomKey, localProjectId)
        }, 800)
    }

    const parseFirestoreValue = (val: any) => {
        if (!val) return null;
        if (val.stringValue !== undefined) return val.stringValue;
        if (val.integerValue !== undefined) return parseInt(val.integerValue, 10);
        if (val.doubleValue !== undefined) return parseFloat(val.doubleValue);
        if (val.booleanValue !== undefined) return val.booleanValue;
        if (val.timestampValue !== undefined) return val.timestampValue;
        if (val.nullValue !== undefined) return null;
        return null; // fallback
    }

    const handleFetchPastData = async () => {
        if (!customFirebaseKey || !customFirebaseProjectId) {
            alert("Error: You must connect a custom Firebase Project ID and API Key first.");
            return;
        }
        if (!workspaceId) {
            alert("Error: Your Workspace ID is missing. Please refresh the page to initialize your workspace.");
            return;
        }
        setIsImporting(true)
        setImportMessage("Fetching data from NEX X Firebase...")
        try {
            const url = `https://firestore.googleapis.com/v1/projects/${customFirebaseProjectId}/databases/(default)/documents/applications?key=${customFirebaseKey}`
            const res = await fetch(url, { cache: 'no-store' });
            const data = await res.json();

            if (data.error) {
                throw new Error(data.error.message || "Failed to fetch from custom Firebase");
            }

            const docs = data.documents || [];
            if (docs.length === 0) {
                setImportMessage("✅ Found 0 applications to import.");
                setIsImporting(false);
                return;
            }

            setImportMessage(`Found ${docs.length} applications. Importing to HRX...`)
            let count = 0;
            const appsRef = collection(db, 'workspaces', workspaceId, 'applications');

            for (const d of docs) {
                const fields = d.fields || {};
                const parsedApp = {
                    name: parseFirestoreValue(fields.name) || "Unknown",
                    email: parseFirestoreValue(fields.email) || "",
                    phone: parseFirestoreValue(fields.phone) || "",
                    portfolio: parseFirestoreValue(fields.portfolio) || "",
                    position: parseFirestoreValue(fields.position) || "General",
                    positionSlug: parseFirestoreValue(fields.positionSlug) || "",
                    positionType: parseFirestoreValue(fields.positionType) || "",
                    resumeFileName: parseFirestoreValue(fields.resumeFileName) || "",
                    resumeMarkdown: parseFirestoreValue(fields.resumeMarkdown) || "",
                    coverLetter: parseFirestoreValue(fields.coverLetter) || "",
                    resumeUrl: parseFirestoreValue(fields.resumeUrl) || "",
                    status: parseFirestoreValue(fields.status) || "new",
                    appliedAt: parseFirestoreValue(fields.appliedAt) || new Date().toISOString(),
                    aiScore: parseFirestoreValue(fields.aiScore) || null,
                    imported: true
                }
                await addDoc(appsRef, parsedApp);
                count++;
            }

            setImportMessage(`✅ Successfully imported ${count} Applications!`);
        } catch (err: any) {
            console.error(err)
            setImportMessage(`❌ Error: ${err.message}`)
        } finally {
            setIsImporting(false);
            setTimeout(() => {
                if (importMessage.includes("✅")) {
                    setImportMessage("")
                }
            }, 4000)
        }
    }

    const isConnected = !!customFirebaseKey && customFirebaseKey === localCustomKey && !!customFirebaseProjectId && customFirebaseProjectId === localProjectId;

    return (
        <div>
            <div className="page-header" style={{ marginBottom: 32 }}>
                <h1 className="page-title">Settings</h1>
            </div>

            <div style={{ display: 'grid', gap: 24, maxWidth: 800 }}>

                <div className="card">
                    <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Workspace Details</h2>
                    <div style={{ display: 'grid', gap: 16 }}>
                        <div>
                            <label className="label">Workspace Name</label>
                            <input className="input" value={workspaceName || ''} readOnly style={{ opacity: 0.7 }} />
                        </div>
                        <div>
                            <label className="label">Workspace ID</label>
                            <input className="input" value={workspaceId || ''} readOnly style={{ fontFamily: 'monospace', fontSize: 12, opacity: 0.7 }} />
                        </div>
                        <div>
                            <label className="label">Current Plan</label>
                            <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--accent)' }}>{plan}</span>
                                <span className="badge badge-reviewed">Upgrade</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <Key size={18} color="var(--accent)" />
                        <h2 style={{ fontSize: 16, fontWeight: 700 }}>API Configuration</h2>
                    </div>

                    <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
                        Use this API key to authenticate requests from the HRX Plugin or your custom integration. Keep it secure.
                    </p>

                    <div style={{ marginBottom: 24 }}>
                        <label className="label">Live API Key</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input
                                className="input"
                                value={mockKey}
                                readOnly
                                style={{ fontFamily: 'monospace', flex: 1, letterSpacing: 0.5 }}
                                type="password"
                            />
                            <button className="btn btn-ghost" onClick={handleCopy} style={{ flexShrink: 0 }}>
                                {copied ? 'Copied!' : <><Copy size={14} /> Copy</>}
                            </button>
                        </div>
                    </div>

                    <div style={{ marginBottom: 12 }}>
                        <h3 style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--text)' }}>Plugin Installation</h3>
                        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                            Add this script to the <code>&lt;head&gt;</code> of your website. Valid forms with <code>data-hrx</code> will automatically be captured.
                        </p>
                        <div style={{ background: '#050508', padding: 16, borderRadius: 8, border: '1px solid var(--border)', position: 'relative' }}>
                            <code style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#a78bfa', wordBreak: 'break-all' }}>
                                &lt;script src="https://hrx-plugin.pages.dev/hrx-plugin.js" async defer data-key="YOUR_API_KEY"&gt;&lt;/script&gt;
                            </code>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <Terminal size={18} color="var(--accent)" />
                        <h2 style={{ fontSize: 16, fontWeight: 700 }}>Custom Integrations</h2>
                    </div>

                    <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
                        Connect your own external services to HRX Agent. Enable this option to sync ingested applications directly to your own Firebase project and import legacy data.
                    </p>

                    <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                            <div style={{ flex: 1 }}>
                                <label className="label">Firebase Project ID</label>
                                <input
                                    className="input"
                                    placeholder="e.g. nex-x-careers"
                                    value={localProjectId}
                                    onChange={(e) => setLocalProjectId(e.target.value)}
                                    disabled={isConnecting || isConnected}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label className="label">Firebase API Key</label>
                                <input
                                    className="input"
                                    placeholder="AIzaSy..."
                                    value={localCustomKey}
                                    onChange={(e) => setLocalCustomKey(e.target.value)}
                                    disabled={isConnecting || isConnected}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <button
                                className="btn btn-primary"
                                style={{ background: isConnected ? 'green' : '' }}
                                onClick={handleConnectFirebase}
                                disabled={isConnecting || isConnected}
                            >
                                {isConnecting ? 'Connecting...' : isConnected ? 'Connected ✅' : 'Connect Custom Firebase'}
                            </button>
                        </div>
                        <p style={{ fontSize: 13, color: 'var(--accent)', marginTop: 16, fontWeight: 500, background: 'rgba(167, 139, 250, 0.1)', padding: '10px 14px', borderRadius: 6, borderLeft: '3px solid var(--accent)' }}>
                            💡 Connection will let you fetch legacy data and automatically sync new applications from HRX straight into your project's `applications` collection.
                        </p>

                        {isConnected && (
                            <div style={{ marginTop: 20, padding: 16, background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', borderRadius: 8 }}>
                                <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Import Legacy Data</h4>
                                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                                    Fetch your past Job and Internship applications from the connected Nexx database into HRX Agent.
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <button
                                        className="btn btn-outline"
                                        style={{ fontSize: 12, padding: '8px 16px', display: 'flex', gap: 8, alignItems: 'center' }}
                                        onClick={handleFetchPastData}
                                        disabled={isImporting}
                                    >
                                        {isImporting ? <div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }}></div> : null}
                                        {isImporting ? 'Importing...' : 'Fetch Past Applications'}
                                    </button>
                                </div>
                                {importMessage && (
                                    <p style={{ marginTop: 12, fontSize: 13, color: importMessage.includes('❌') ? 'var(--error)' : 'green' }}>
                                        {importMessage}
                                    </p>
                                )}
                            </div>
                        )}

                        {isConnected && (
                            <button className="btn btn-ghost" style={{ marginTop: 16, color: 'var(--error)', padding: 0 }} onClick={() => { setCustomFirebaseKey(null, null); setLocalCustomKey(''); setLocalProjectId(''); }}>
                                Disconnect Integration
                            </button>
                        )}
                    </div>
                </div>

            </div>
        </div>
    )
}
